import { cloneElement, useId } from 'react';
import styled from '@emotion/styled';

import { FieldLabel } from '../FieldLabel';
import { Message } from '../Message';

import type { FormControlProps } from './FormControl.types';
import type { Theme } from '../../theme';

/**
 * Container for the entire form control
 */
const FormControlContainer = styled.div<{
  theme?: Theme;
  $fullWidth: boolean;
}>`
  display: flex;
  flex-direction: column;
  ${({ $fullWidth }) => $fullWidth && 'width: 100%;'}
  align-items: flex-start;
  gap: 4px;
`;

/**
 * Container for the label row (label + optional right adornment)
 */
const LabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

/**
 * Container for the label and any additional elements
 */
const LabelContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

/**
 * FormControl - A wrapper component that provides layout for form fields
 *
 * Combines label, input, and message components into a consistent form field layout.
 * Uses FieldLabel, ErrorMessage, and AssistiveMessage components internally.
 *
 * @example
 * ```tsx
 * // Basic usage with label
 * <FormControl label="Username" required>
 *   <Input placeholder="Enter username..." />
 * </FormControl>
 *
 * // With error state
 * <FormControl
 *   label="Email"
 *   error
 *   errorMessage="Please enter a valid email"
 * >
 *   <Input type="email" />
 * </FormControl>
 *
 * // With assistive text
 * <FormControl
 *   label="Password"
 *   assistiveText="Must be at least 8 characters"
 * >
 *   <Input type="password" />
 * </FormControl>
 *
 * // With right adornment
 * <FormControl
 *   label="API Key"
 *   labelRightAdornment={<Button size="sm">Generate</Button>}
 * >
 *   <Input />
 * </FormControl>
 * ```
 */
export const FormControl: React.FC<FormControlProps> = ({
  children,
  label,
  labelFor,
  required = false,
  error = false,
  errorMessage,
  assistiveText,
  id,
  className,
  fullWidth = true,
  'data-testid': testId,
  dataTestId,
  dataId,
  labelRightAdornment,
  disabled = false,
}) => {
  const generatedId = useId();
  const controlId = labelFor || generatedId;

  // Get the child element's existing props safely
  const childProps = children.props as Record<string, unknown>;
  const childId = childProps.id as string | undefined;
  const childDisabled = childProps.disabled as boolean | undefined;

  // Clone the child element with additional props
  const enhancedChild = cloneElement(children, {
    id: childId || controlId,
    'aria-invalid': error || undefined,
    'aria-required': required || undefined,
    disabled: disabled || childDisabled,
  } as Record<string, unknown>);

  // If no label, just render the control with messages
  if (!label) {
    return (
      <FormControlContainer
        id={id}
        className={className}
        $fullWidth={fullWidth}
        data-testid={dataTestId || testId}
        data-id={dataId}
      >
        {enhancedChild}
        {error && errorMessage ? (
          <Message variant="error" size="sm" dataTestId={testId ? `${testId}-error` : undefined}>
            {errorMessage}
          </Message>
        ) : assistiveText ? (
          <Message variant="assistive" size="sm" dataTestId={testId ? `${testId}-assistive` : undefined}>
            {assistiveText}
          </Message>
        ) : null}
      </FormControlContainer>
    );
  }

  return (
    <FormControlContainer
      id={id}
      className={className}
      $fullWidth={fullWidth}
      data-testid={dataTestId || testId}
      data-id={dataId}
    >
      {labelRightAdornment ? (
        <LabelRow>
          <LabelContainer>
            <FieldLabel
              label={typeof label === 'string' ? label : ''}
              htmlFor={controlId}
              required={required}
              data-testid={testId ? `${testId}-label` : undefined}
            />
          </LabelContainer>
          {labelRightAdornment}
        </LabelRow>
      ) : (
        <LabelContainer>
          <FieldLabel
            label={typeof label === 'string' ? label : ''}
            htmlFor={controlId}
            required={required}
            data-testid={testId ? `${testId}-label` : undefined}
          />
        </LabelContainer>
      )}
      {enhancedChild}
      {error && errorMessage ? (
        <Message variant="error" size="sm" dataTestId={testId ? `${testId}-error` : undefined}>
          {errorMessage}
        </Message>
      ) : assistiveText ? (
        <Message variant="assistive" size="sm" dataTestId={testId ? `${testId}-assistive` : undefined}>
          {assistiveText}
        </Message>
      ) : null}
    </FormControlContainer>
  );
};

FormControl.displayName = 'FormControl';

export default FormControl;
